<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">上报记录</span>
      <span class="nav-right"><button class="refresh-btn" @click="reload"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <div v-if="!gatherEnabled" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Upload /></el-icon>
        <p class="sub-text">信息收集后台未配置（构建时设置 VITE_API_BASE）</p>
      </div>

      <template v-else>
        <GatherAuthCard v-if="showAuth" @authed="onAuthed" @skip="showAuth = false" />
        <template v-else>
        <div class="search-line">
          <el-input v-model="keyword" placeholder="搜索记录码 / 上传人 / 备注" clearable size="default" @keyup.enter="onSearch" @clear="onSearch">
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          <el-button size="default" circle @click="dateVisible = true"><el-icon><Filter /></el-icon></el-button>
        </div>

        <div class="chip-row">
          <span class="chip" :class="{ on: statusSel === '' }" @click="setStatus('')">全部</span>
          <span class="chip" :class="{ on: statusSel === '未处理' }" @click="setStatus('未处理')">未处理</span>
          <span class="chip" :class="{ on: statusSel === '处理中' }" @click="setStatus('处理中')">处理中</span>
          <span class="chip" :class="{ on: statusSel === '已处理' }" @click="setStatus('已处理')">已处理</span>
        </div>

        <!-- 时间段：默认仅当天，其他时段手动选择 -->
        <div class="chip-row">
          <span v-for="r in ranges" :key="r.key" class="chip" :class="{ on: quickSel === r.key }" @click="setQuick(r.key)">{{ r.label }}</span>
          <span class="chip" :class="{ on: quickSel === 'custom' }" @click="dateVisible = true"><el-icon :size="12"><Filter /></el-icon> 自定义</span>
        </div>
        <p class="data-tip">默认仅显示当天的上报记录，其他时段请手动选择。</p>

        <p v-if="dateText || keyword" class="active-filters">
          筛选：<el-tag v-if="dateText" size="small" closable @close="clearDate">{{ dateText }}</el-tag>
          <el-tag v-if="keyword" size="small" type="info" closable @close="keyword = ''; onSearch()">{{ keyword }}</el-tag>
        </p>

        <div v-if="loading" class="center-box">
          <el-icon class="is-loading" :size="28"><Loading /></el-icon>
          <p class="sub-text">加载中…</p>
        </div>
        <div v-else-if="error" class="white-card center-box">
          <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
          <p class="sub-text">{{ error }}</p>
          <el-button size="small" type="primary" plain style="margin-top:10px" @click="reload">重试</el-button>
        </div>
        <div v-else-if="records.length === 0" class="white-card center-box">
          <el-icon :size="42" color="#c0c4cc"><Box /></el-icon>
          <p class="sub-text">没有找到相关记录</p>
        </div>

        <template v-else>
          <div class="g-item" v-for="r in records" :key="r.id" @click="open(r)">
            <div class="g-top">
              <span class="g-id mono">{{ r.id }}</span>
              <span class="pill" :class="statusPill(r.status)">{{ r.status }}</span>
            </div>
            <div class="g-desc">{{ r.description || '（无备注）' }}</div>
            <div class="g-media-tags" v-if="(r.images || []).length || (r.videos || []).length">
              <span v-if="(r.images || []).length" class="tag">📷 {{ r.images.length }} 张照片</span>
              <span v-if="(r.videos || []).length" class="tag">🎬 {{ r.videos.length }} 段视频</span>
            </div>
            <div class="g-meta">
              <span v-if="r.reporter">👤 {{ r.reporter }}</span>
              <span>🕒 {{ fmt(r.reportTime) }}</span>
              <span v-if="r.synced === false" class="sync">未同步</span>
            </div>
          </div>

          <div v-if="records.length < total" class="load-more">
            <el-button v-if="!loadingMore" text type="primary" @click="loadMore">加载更多（{{ records.length }}/{{ total }}）</el-button>
            <span v-else class="sub-text"><el-icon class="is-loading"><Loading /></el-icon> 加载中…</span>
          </div>
          <p v-else-if="records.length" class="no-more">— 已经到底了 —</p>
        </template>
        </template>
      </template>
    </div>

    <el-drawer v-model="dateVisible" title="按日期筛选" size="72%" direction="btt">
      <div class="drawer-body">
        <el-form label-position="top">
          <el-form-item label="开始日期">
            <el-date-picker v-model="startDate" type="date" placeholder="开始" value-format="YYYY-MM-DD" style="width:100%" />
          </el-form-item>
          <el-form-item label="结束日期">
            <el-date-picker v-model="endDate" type="date" placeholder="结束" value-format="YYYY-MM-DD" style="width:100%" />
          </el-form-item>
        </el-form>
        <div class="drawer-actions">
          <el-button @click="clearDate" style="width:30%">重置</el-button>
          <el-button type="primary" @click="applyDate" style="width:65%">确定</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchGatherRecords, gatherEnabled, isAuthError } from '../api'
import GatherAuthCard from '../components/GatherAuthCard.vue'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
function back() { router.back() }

const showAuth = ref(false)

function onAuthed() {
  showAuth.value = false
  load(true)
}

const records = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = 20
const loading = ref(true)
const loadingMore = ref(false)
const error = ref('')
const keyword = ref('')
const statusSel = ref(route.query.status || '')
const startDate = ref('')
const endDate = ref('')
const dateVisible = ref(false)
const quickSel = ref('today')

const ranges = [
  { key: 'today', label: '今天' },
  { key: 'week', label: '近7天' },
  { key: 'month', label: '本月' },
  { key: 'all', label: '全部' }
]

function todayStr() { return dayjs().format('YYYY-MM-DD') }

// 默认：仅当天（如需其他时段手动选择）
if (route.query.startDate && route.query.endDate) {
  startDate.value = String(route.query.startDate)
  endDate.value = String(route.query.endDate)
  quickSel.value = 'custom'
} else {
  startDate.value = todayStr()
  endDate.value = todayStr()
}

function setQuick(key) {
  quickSel.value = key
  const today = todayStr()
  if (key === 'today') {
    startDate.value = today
    endDate.value = today
  } else if (key === 'week') {
    startDate.value = dayjs().subtract(6, 'day').format('YYYY-MM-DD')
    endDate.value = today
  } else if (key === 'month') {
    startDate.value = dayjs().startOf('month').format('YYYY-MM-DD')
    endDate.value = today
  } else {
    startDate.value = ''
    endDate.value = ''
  }
  load(true)
}

const dateText = computed(() => {
  if (startDate.value && endDate.value) {
    if (startDate.value === endDate.value) return startDate.value + '（当天）'
    return `${startDate.value} ~ ${endDate.value}`
  }
  if (startDate.value) return `${startDate.value} 起`
  if (endDate.value) return `至 ${endDate.value}`
  return ''
})

function fmt(t) { return t ? dayjs(t).format('MM-DD HH:mm') : '' }
function statusPill(s) {
  if (s === '已处理') return 'pill-green'
  if (s === '处理中') return 'pill-orange'
  return 'pill-red'
}

function buildParams() {
  return {
    page: page.value,
    pageSize,
    status: statusSel.value || '',
    startDate: startDate.value || '',
    endDate: endDate.value || ''
  }
}

async function fetchPage() {
  // 后台 list 不支持关键字，先服务端分页再本地过滤
  const data = await fetchGatherRecords(buildParams())
  const kw = keyword.value.trim().toLowerCase()
  if (!kw) return data
  const filtered = (data.data || []).filter(r =>
    (r.id || '').toLowerCase().includes(kw) ||
    (r.reporter || '').toLowerCase().includes(kw) ||
    (r.description || '').toLowerCase().includes(kw)
  )
  return { ...data, data: filtered }
}

async function load(reset = true) {
  if (reset) {
    page.value = 1
    loading.value = true
  } else {
    loadingMore.value = true
  }
  try {
    const r = await fetchPage()
    if (reset) {
      records.value = r.data || []
      total.value = r.total || 0
    } else {
      records.value = records.value.concat(r.data || [])
    }
  } catch (e) {
    if (isAuthError(e)) {
      showAuth.value = true
      error.value = ''
    } else {
      error.value = e.message || '加载失败'
    }
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

function reload() { load(true) }
function onSearch() { load(true) }
function setStatus(s) { statusSel.value = s; load(true) }
function loadMore() { page.value += 1; load(false) }

function applyDate() {
  quickSel.value = 'custom'
  dateVisible.value = false
  load(true)
}
function clearDate() {
  startDate.value = ''
  endDate.value = ''
  quickSel.value = 'all'
  dateVisible.value = false
  load(true)
}

function open(r) {
  router.push(`/gather-record/${r.id}`)
}

onMounted(() => load(true))
</script>

<style scoped>
.search-line { display: flex; gap: 8px; margin-bottom: 8px; }
.search-line .el-input { flex: 1; }
.active-filters { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #909399; margin-bottom: 8px; flex-wrap: wrap; }
.data-tip { font-size: 11px; color: #b0b3bb; margin: -4px 2px 8px; }

.g-item { background: #fff; border-radius: 14px; padding: 12px 14px; margin-bottom: 10px; box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; }
.g-item:active { transform: scale(.99); }
.g-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.g-id { font-size: 12.5px; font-weight: 700; color: #5f6368; }
.g-desc { font-size: 14px; color: #202124; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.g-media-tags { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.g-media-tags .tag { font-size: 11.5px; color: #5f6368; background: #f1f3f4; border-radius: 8px; padding: 2px 9px; }
.g-meta { display: flex; gap: 9px; font-size: 11.5px; color: #9aa0a6; margin-top: 8px; flex-wrap: wrap; }
.sync { color: #b06000; }
.load-more { text-align: center; padding: 6px 0; }
.no-more { text-align: center; color: #c0c4cc; font-size: 12px; padding: 12px 0; }
.drawer-body { padding: 4px; }
.drawer-actions { display: flex; gap: 10px; margin-top: 16px; }
</style>
